import { ReactNode, useEffect, useState } from 'react';
import { useLocation, Outlet, useMatch } from 'react-router-dom';
import Header from './AuthLayout/Header';
import Sidebar from './AuthLayout/Sidebar';
import { ModuleName } from '../../types/navigation';
import { UserRole } from '../../types/users';
import { useAppSelector } from '../../store';
import { selectAuthUser, selectRoles } from '../../store/slices/auth/auth.selector';
import { useMemo } from 'react';

import { sidebarConfig as modularSidebarConfig } from '../../config/sidebar';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { useGetCurrentUserQuery } from '../../service/user/useGetCurrentUserQuery';
import { setUserProfile } from '../../store/slices/auth/auth.slice';
import { useProjectsSummaryQuery } from 'service/project';
import { MODULES } from '../common/constants';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { getDefaultRouteByRole } from '../../routes/helpers';

interface LayoutProps {
  children?: ReactNode;
}

// Account Setup Layout
const AccountSetupLayout = ({ children }: LayoutProps) => {
  return (
    <div
      className={`min-h-screen lg:h-screen w-full bg-[#0C1017] align-element pt-6 overflow-y-auto lg:overflow-hidden flex flex-col !text-left`}
    >
      {children}
    </div>
  );
};

// Base layout component
const BaseAuthenticatedLayout = ({
  children,
  role,
}: LayoutProps & { role: UserRole }) => {
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser)
  const dispatch = useAppDispatch();
  const { data: profile } = useGetCurrentUserQuery(!!authUser);

 const SIDEBAR_KEY = 'pmo_sidebar_collapsed';
 const [collapsed, setCollapsed] = useState<boolean>(() => {
   try {
     const stored = localStorage.getItem(SIDEBAR_KEY);
     return stored !== null ? stored === 'true' : false;
   } catch {
     return false;
   }
 });
 const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const userRoles = useAppSelector(selectRoles);
  const isOrgAdmin = userRoles.some(role => ["ORG_ADMIN", "ORG_OWNER"].includes(role));
  const hasHrmsAccess = isOrgAdmin || userRoles.some(role => ["HRM_ADMIN", "HRM_MANAGER", "HRM_EMPLOYEE", "EMPLOYEE"].includes(role));
  const hasPmoAccess = isOrgAdmin || userRoles.some(role => ["PMO_ADMIN", "PMO_MANAGER", "PMO_USER", "EMPLOYEE"].includes(role));
  const hasCrmAccess = isOrgAdmin || userRoles.some(role => ["CRM_ADMIN", "CRM_MANAGER", "CRM_USER"].includes(role));

  const visibleModules = MODULES.filter((module) => {
    if (module.id === 'HRMS' && !hasHrmsAccess) return false;
    if (module.id === 'Project Management' && !hasPmoAccess) return false;
    if (module.id === 'CRM' && !hasCrmAccess) return false;
    return true;
  });

  const { toProjects } = usePmoNavigation();

  const handleModuleSelect = (moduleId: ModuleName) => {
    setActiveModule(moduleId);
    setMobileSidebarOpen(false);
    switch (moduleId) {
      case "Project Management":
        toProjects();
        break;
      case "CRM":
        if (authUser?.orgSlug) {
          navigate(`/${authUser.orgSlug}/crm/contacts`);
        } else {
          navigate("/overview");
        }
        break;
      case "HRMS":
      default:
        if (authUser?.orgSlug) {
          navigate(`/${authUser.orgSlug}/hrms/dashboard`);
        } else {
          navigate("/overview");
        }
        break;
    }
  };

 const handleCollapse = (value: boolean) => {
   try {
     localStorage.setItem(SIDEBAR_KEY, String(value));
   } catch { /* storage unavailable — proceed silently */ }
   setCollapsed(value);
 };

  useEffect(() => {
    if (profile) {
      dispatch(setUserProfile(profile));
    }
  }, [profile, dispatch]);

  const {
     data: projectsResponse,
   } = useProjectsSummaryQuery();
   const projects = projectsResponse?.data || [];

  const { pathname } = useLocation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );

  const matchWithoutKey = useMatch('/:orgSlug/pmo/:id');
  const matchWithKey = useMatch('/:orgSlug/pmo/:id/:key');
  const matchWithSubKey = useMatch('/:orgSlug/pmo/:id/:key/:subkey');

  // Then determine which match to use
  const match = matchWithoutKey || matchWithKey || matchWithSubKey;
  const isProjectRoute = Boolean(match);
  // Determine default module based on user role
  const getDefaultModule = (): ModuleName => {
    if (["ORG_OWNER", "ORG_ADMIN", "HRM_ADMIN", "HRM_MANAGER", "HRM_EMPLOYEE", "EMPLOYEE"].includes(role)) {
      return 'HRMS';
    }
    if (["PMO_ADMIN", "PMO_MANAGER", "PMO_USER"].includes(role)) {
      return 'Project Management';
    }
    if (["CRM_ADMIN", "CRM_MANAGER", "CRM_USER"].includes(role)) {
      return 'CRM';
    }
    return 'HRMS';
  };

  const [activeModule, setActiveModule] = useState<ModuleName>(
    getDefaultModule()
  );



  const { orgSlug } = authUser ?? { orgSlug: 'org' };

  const computedSidebarConfig = useMemo(() => {
    // Get the current orgSlug from URL or auth state
    const currentOrgSlug = orgSlug || pathname.split('/').filter(Boolean)[0] || 'org';
    
    // Normalize role string to match config keys (e.g., "Admin" -> "ORG_ADMIN")
    const normalizedRole = (role || 'EMPLOYEE').toUpperCase().includes('ADMIN') && !role.includes('_') 
      ? `ORG_${role.toUpperCase()}` as UserRole 
      : (role as UserRole);

    // Resolve which role's config to use, with a fallback
    const roleConfig = modularSidebarConfig[normalizedRole] || modularSidebarConfig[role] || modularSidebarConfig['EMPLOYEE'];
    
    // Support both 'Project Management' and 'pmo' keys for flexibility
    // Default to Project Management if we are in a pmo path
    const effectiveModule = (activeModule === 'Project Management' || pathname.includes('/pmo/')) ? 'Project Management' : activeModule;
    const base = roleConfig[effectiveModule] || roleConfig['Project Management'] || modularSidebarConfig['EMPLOYEE']['Project Management'] || [];

    const resolvePaths = (items: any[]): any[] => {
      return items.map((item) => {
        const newItem = { ...item };
        if (newItem.path) {
          newItem.path = newItem.path.replace(':orgSlug', currentOrgSlug);
        }
        if (newItem.children) {
          newItem.children = resolvePaths(newItem.children);
        }
        return newItem;
      });
    };

    const resolvedBase = resolvePaths(base);

    if (effectiveModule !== 'Project Management') return resolvedBase;

    return resolvedBase.map((group: any) => {
      if (group.id === 'projects-group') {
        const projectLinks = [
          ...(projects ?? []).map((project: any) => ({
            id: `project-${project.projectUuid}`,
            label: project.name,
            path: `/${currentOrgSlug}/pmo/${project.projectKey}`,
            type: 'link' as const,
          })),
        ];

        return {
          ...group,
          children: [...(group.children ?? []), ...projectLinks],
        };
      }
      return group;
    });
  }, [projects, role, activeModule, orgSlug, pathname]);

  useEffect(() => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const moduleSegment = pathSegments[1]; // /orgSlug/module/path

    if (moduleSegment === 'pmo') {
      setActiveModule('Project Management');
    } else if (moduleSegment === 'hrms') {
      setActiveModule('HRMS');
    } else if (moduleSegment === 'crm') {
      setActiveModule('CRM');
    } else if (pathname.includes('settings')) {
      // Keep current module or default
    }
  }, [pathname]);
  useEffect(() => {
    if (pathname === '/' && authUser?.isOrgOnboarded) {
      const orgSlug = authUser?.orgSlug || 'org';
      const targetRoute = getDefaultRouteByRole(userRoles, orgSlug);
      navigate(targetRoute, { replace: true });
    }
  }, [pathname, navigate, authUser?.isOrgOnboarded, authUser?.orgSlug, userRoles]);

  if (
    pathname.includes('account-setup') ||
    pathname.includes('onboarding-success')
  ) {
    return <AccountSetupLayout>{children}</AccountSetupLayout>;
  }

  const pathSegments = pathname.split('/').filter(Boolean);

  // Check if sidebar should be hidden - only for superAdmin on overview page specifically
  // const shouldHideSidebar =
  //   role === 'ORG_OWNER' &&
  //   (pathname === '/' || pathname.includes('overview')) &&
  //   activeModule === 'Overview';

  /**
   * Only the cross-module settings area — /:orgSlug/settings/… (users,
   * organization, profile) — gives up the sidebar: it is a section of its own
   * with its own navigation, and nothing in the module nav applies there.
   *
   * A module's own settings are still that module, so /:orgSlug/hrms/settings
   * and /:orgSlug/crm/settings keep the sidebar and the way back out. Matched
   * on the segment rather than `includes('/settings')`, which could not tell
   * the two apart and needed a named exception per module.
   */
  const shouldHideSidebar = pathSegments[1] === 'settings';

  // Viewport-locked layout: all /pmo routes EXCEPT scrollable form pages (create/edit project)
  const isPmo = pathSegments.includes('pmo');
  
  // 1. All PMO routes are "Tight" by default to maximize space,
  // EXCEPT for the initial project creation and editing forms, and Settings —
  // a page of forms that wants the same padded, scrolling shell HRMS and CRM
  // settings get.
  const isPmoSettings = isPmo && pathSegments[2] === 'settings';
  const isPmoSpacious = isPmo && (
    isPmoSettings ||
    (pathSegments.length <= 3 && ['create', 'edit'].includes(pathSegments[pathSegments.length - 1]))
  );

  const isTightPmo = isPmo && !isPmoSpacious;


  const isCrm = pathSegments[1] === 'crm';

  const CRM_STATIC_SEGMENTS = [
    'dashboard',
    'contacts',
    'companies',
    'deals',
    'tasks',
    'reports',
    'create',
  ];
  const isCrmContactDetail =
    isCrm &&
    pathSegments.length === 3 &&
    !CRM_STATIC_SEGMENTS.includes(pathSegments[2]);

  const CRM_DETAIL_PARENTS = ['deals', 'companies', 'contacts'];
  const isCrmRecordDetail =
    isCrm &&
    pathSegments.length === 4 &&
    CRM_DETAIL_PARENTS.includes(pathSegments[2]) &&
    pathSegments[3] !== 'create';
  const isTightCrm = isCrmContactDetail || isCrmRecordDetail;

  // 2. Determine if it's a locked view (Board/List) or scrollable view (Task Detail)
  const isLockedPmo = isTightPmo && (
    pathname.includes('/projects') || 
    pathname.includes('/backlog') || 
    pathname.includes('/board') ||
    (pathSegments.length === 3 && pathSegments[1] === 'pmo')
  );

  const isTaskDetailOrCreate = isTightPmo && !isLockedPmo;
  const isPmoRoute = isLockedPmo;
  const isScrollOnlyPmoRoute = isTaskDetailOrCreate;

  return (
      <div className='h-screen w-full overflow-hidden bg-primary'>
        {/* Fixed Header */}
        <div className='fixed top-0 left-0 right-0 z-50'>
          <Header
            variant={role}
            activeModule={activeModule}
            setActiveModule={setActiveModule}
            onMobileMenuToggle={() => setMobileSidebarOpen(true)}
            hasSidebar={!shouldHideSidebar}
            sidebarCollapsed={collapsed}
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && !shouldHideSidebar && (
          <div
            className="fixed inset-0 z-[60] bg-black/50 min-991:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Fixed Sidebar - conditionally rendered */}
        {!shouldHideSidebar && (
          <div
            className={`fixed top-0 left-0 bottom-0 z-[70] min-991:z-40 transition-all duration-300
      ${collapsed ? 'w-16' : 'w-64'} ${mobileSidebarOpen ? 'translate-x-0' : 'max-991:-translate-x-full'} min-991:translate-x-0`}
          >
            <Sidebar
              variant={role}
              activeModule={activeModule}
              expandedItems={expandedItems}
              setExpandedItems={setExpandedItems}
              config={computedSidebarConfig}
              collapsed={collapsed}
              onCollapse={handleCollapse}
              visibleModules={visibleModules}
              onModuleSelect={handleModuleSelect}
            />
          </div>
        )}

        {/* Main Content - adjust margin based on sidebar visibility */}
       <main
    className={`
      transition-all duration-300
      ${!shouldHideSidebar ? (collapsed ? 'min-991:ml-16' : 'min-991:ml-64') : 'align-element mx-auto'}
      ${isTightPmo
        ? `mt-[70px] flex flex-col min-w-0 min-h-0 ${
            isLockedPmo 
              ? 'h-[calc(100vh-70px)] overflow-hidden' 
              : 'h-[calc(100vh-70px)] overflow-y-auto md:overflow-hidden pb-10 md:pb-0'
          }`
        : `mt-[60px] h-[calc(100vh-70px)] flex flex-col min-h-0 min-w-0 overflow-y-auto pt-6  ${!isTightCrm ? 'px-[24px]' : ''} max-[991px]:px-[20px]`
      }
    `}
  >
    {isTightPmo ? (
      children || <Outlet />
    ) : (
      <div className="mx-auto w-full max-w-[1400px] flex flex-col flex-1 min-h-0">
        {children || <Outlet />}
      </div>
    )}
  </main>
      </div>
  );
};

// Role-specific layouts
const AdminLayout = ({ children }: LayoutProps) => {
  const userRoles = useAppSelector(selectRoles);
  const adminRole = (["ORG_OWNER", "ORG_ADMIN", "PMO_ADMIN", "HRM_ADMIN", "CRM_ADMIN", "HRM_MANAGER", "PMO_MANAGER", "CRM_MANAGER", "PMO_USER", "HRM_EMPLOYEE", "CRM_USER", "EMPLOYEE"] as UserRole[]).find(r =>
    userRoles.includes(r)
  );
  return <BaseAuthenticatedLayout role={adminRole ?? userRoles[0] ?? 'EMPLOYEE'}>{children}</BaseAuthenticatedLayout>;
};

const HRLayout = ({ children }: LayoutProps) => {
  const userRoles = useAppSelector(selectRoles);
  const hrRole = (["ORG_OWNER", "ORG_ADMIN", "HRM_ADMIN", "HRM_MANAGER", "HRM_EMPLOYEE", "EMPLOYEE"] as UserRole[]).find(r =>
    userRoles.includes(r)
  );
  return <BaseAuthenticatedLayout role={hrRole ?? 'EMPLOYEE'}>{children}</BaseAuthenticatedLayout>;
};

const EmployeeLayout = ({ children }: LayoutProps) => (
  <BaseAuthenticatedLayout role='EMPLOYEE'>{children}</BaseAuthenticatedLayout>
);

const PMOLayout = ({ children }: LayoutProps) => {
  const userRoles = useAppSelector(selectRoles);
  // Pick the role that actually grants a PMO sidebar (ORG_* or PMO_*), most
  // privileged first — not just userRoles[0]. Otherwise a user holding an
  // unrelated role earlier in the array (e.g. HRM_EMPLOYEE, whose own
  // Project Management sidebar is unrestricted) would incorrectly get the
  // full sidebar instead of PMO_USER's limited one, even though PMO_USER is
  // the role that actually governs their PMO access.
  const pmoRole = (["ORG_OWNER", "ORG_ADMIN", "PMO_ADMIN", "PMO_MANAGER", "PMO_USER"] as UserRole[]).find(r =>
    userRoles.includes(r)
  );
  return <BaseAuthenticatedLayout role={pmoRole ?? userRoles[0]}>{children}</BaseAuthenticatedLayout>;
}

const CRMLayout = ({ children }: LayoutProps) => {
  const userRoles = useAppSelector(selectRoles);
  // Pick the role that actually grants a CRM sidebar (ORG_* or CRM_*),
  // not just userRoles[0] — otherwise a user whose first role is e.g.
  // HRM_EMPLOYEE would fall back to the Project Management sidebar.
  const crmRole = userRoles.find(role =>
    ["ORG_OWNER", "ORG_ADMIN", "CRM_ADMIN", "CRM_MANAGER", "CRM_USER"].includes(role)
  );
  return <BaseAuthenticatedLayout role={crmRole ?? userRoles[0]}>{children}</BaseAuthenticatedLayout>;
};
const SuperAdminLayout = ({ children }: LayoutProps) => (
  <BaseAuthenticatedLayout role='ORG_OWNER'>
    {children}
  </BaseAuthenticatedLayout>
);

// Non-authenticated layout
const NonAuthLayout = ({ children }: LayoutProps) => {
  return (
    <div className='grid place-items-center min-h-screen max-w-[1500px] bg-primary align-element'>
      <div className='relative grid md:grid-cols-2 gap-2 md:h-[85vh] bg-primary-intemediate rounded-2xl overflow-hidden w-full bg-cover bg-center'>
        <div className='bg-cover bg-center auth-bg relative w-full hidden md:block'>
          <img
            className='relative z-50 ml-6 top-4'
            src='/assets/worklapLogo.svg'
            alt='worklap logo'
          />
          <p className='absolute font-bold text-xl pl-6 bottom-3 z-50'>
            Unleash your workspace's full potential.
          </p>
          <div className='absolute inset-0 bg-gradient-to-b from-[#000000]/60 via-[#000000]/20 to-[#000000]/70' />
        </div>
      <div className='max-w-[550px] flex items-center justify-center px-2 my-2 md:my-4 md:px-4 mx-auto w-full overflow-y-auto max-h-screen'>
  {children}
</div>
      </div>
    </div>
  );
};
export {
  AdminLayout,
  HRLayout,
  EmployeeLayout,
  NonAuthLayout,
  AccountSetupLayout,
  SuperAdminLayout,
  PMOLayout,
  CRMLayout
};